<script setup lang="ts">
const props = defineProps<{
  block: BlockDownloads,
  noHorizontalPadding?: boolean,
}>()

const files = computed(() => props.block.files || []) as ComputedRef<Media[]>

const showCovers = computed(() => props.block.layout === 'covers')

/** the cell a cover is fitted into — width : height */
const SLOT_RATIO = 3 / 4
const SLOT_ASPECT = `${SLOT_RATIO}`

// Only PDFs get a generated cover; images preview themselves. Anything else
// keeps rendering as a plain entry in the grid.
const coverOf = (file: Media) => {
  if (typeof file.cover === 'object' && file.cover) return file.cover
  if (file.mimeType?.startsWith('image/')) return file
  return undefined
}

/**
 * A cover is drawn at its own size inside the cell rather than cropped to it:
 * the units go on whichever of its edges meets the cell and the other edge
 * follows from the aspect ratio. The box is then exactly the rectangle
 * object-contain fits the cover into, so the sheet behind it and the shadow
 * around it are the cover's own, not the cell's.
 */
const boxOf = (cover: Media | MediaCover) => {
  const ratio = cover.width && cover.height ? cover.width / cover.height : SLOT_RATIO
  return {
    width: ratio >= SLOT_RATIO ? '100%' : `${(ratio / SLOT_RATIO) * 100}%`,
    aspectRatio: `${ratio}`,
  }
}

// Editors name a file for the CMS, not for the page: the caption is the title
// worth reading, so it stands in for the filename wherever one was written.
const titleOf = (file: Media) => richTextToString(file.caption) || file.filename

const niceBytes = (x: number) => {
  let l = 0, n = parseInt(String(x), 10) || 0
  while (n >= 1024 && ++l) { n = n / 1024 }
  return (n.toFixed(n < 10 && l > 0 ? 1 : 0) + ' ' + ['bytes', 'KiB', 'MiB', 'GiB', 'TiB'][l])
}
</script>

<template>
  <section class="container-d my-3" :class="{ 'px-1': !noHorizontalPadding }">
    <h2 v-if="block.label" class="label">
      {{ block.label }}
    </h2>

    <ul :class="showCovers ? 'grid grid-cols-2 md:grid-cols-4 gap-1' : undefined">
      <li
        v-for="(file, index) in files"
        :key="index"
        class="group border-current"
        :class="{ 'list-sep': !showCovers }"
      >
        <a
          :href="file.url"
          target="_blank"
          rel="noopener noreferrer"
          class="group block focus:outline-none "
        >
          <div
            v-if="showCovers"
            class="relative mb-0_35"
            :class="{ 'bg-gray shadow-[0_0_1em_rgba(0,0,0,0.025)]': !coverOf(file) }"
            :style="{ aspectRatio: SLOT_ASPECT }"
          >
            <!-- inset-0 + m-auto centres the sized box in its cell -->
            <div
              v-if="coverOf(file)"
              class="absolute inset-0 m-auto bg-white shadow-[0_0_1em_rgba(0,0,0,0.025)] group-hover:opacity-75 group-focus:opacity-75 transition-opacity"
              :style="boxOf(coverOf(file)!)"
            >
              <core-image
                :image="coverOf(file)!"
                :apply-aspect-ratio-style="false"
                class="w-full h-full object-contain"
              />
            </div>
          </div>

          <div class="group-hover:color-accent text-xs text-current flex flex-wrap gap-x-0_25 items-baseline">
            {{ titleOf(file) }}
          </div>
          <div class="group-hover:color-accent text-xs">
            {{ niceBytes(file?.filesize) }}–{{ file?.mimeType?.split('/')[1]?.toUpperCase() }}
          </div>
        </a>
      </li>
    </ul>
  </section>
</template>
